import { ImgHTMLAttributes } from 'react';

interface OptimizedImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
}

/**
 * Optimized image component that serves WebP format with PNG/JPG fallback
 *
 * Usage:
 * <OptimizedImage
 *   src="/media/images/example.png"
 *   alt="Description"
 *   className="myClass"
 * />
 *
 * This will automatically:
 * - Serve WebP to browsers that support it (Chrome, Firefox, Edge, Safari 14+)
 * - Fall back to original format for older browsers
 * - Maintain all standard img attributes (className, style, etc.)
 */
const OptimizedImage = ({ src, alt, ...props }: OptimizedImageProps) => {
  // Extract the file extension and path
  const getWebPPath = (originalSrc: string): string => {
    // Handle both .png and .jpg/.jpeg
    return originalSrc.replace(/\.(png|jpe?g)$/i, '.webp');
  };

  const webpSrc = getWebPPath(src);

  return (
    <picture style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      {/* WebP source for modern browsers */}
      <source srcSet={webpSrc} type="image/webp" />

      {/* Fallback for browsers that don't support WebP */}
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        {...props}
      />
    </picture>
  );
};

export default OptimizedImage;
